import { useCallback, useEffect, useRef, useState } from "react";
import type { FC } from "react";

import { GetCourseDetails } from "../../handler/coursehandler";
import VideoPlayer from "./VideoPlayer";

interface InnerBannerProps {
    courseId: number;
}

interface Lesson {
    courseVideoId: number;
    courseServerName:string;
    courseVideoName: string;
    videoFileName: string;
    totalVideoDurationInSeconds: number;
    currentVideoDurationInSeconds: number;
    courseId: number;
    videoPercentage: number;
}

interface CourseLearningData {
    courseId: number;
    courseName: string;
    serverName: string;
    courseDescription: string;
    packageId: number;
    coursePrice: number;
    percentage: number;
    currentVideoId: number;
    courseVideos: Lesson[];
}

const emptyLesson: Lesson = {
    courseVideoId: 0,
    courseServerName:"",
    courseVideoName: "",
    videoFileName: "",
    totalVideoDurationInSeconds: 0,
    currentVideoDurationInSeconds: 0,
    courseId: 0,
    videoPercentage: 0,
};

const emptyCourse: CourseLearningData = {
    courseId: 0,
    courseName: "",
    serverName: "",
    courseDescription: "",
    packageId: 0,
    coursePrice: 0,
    percentage: 0,
    currentVideoId: 0,
    courseVideos: [],
};

const Learning: FC<InnerBannerProps> = ({ courseId }) => {
    const loadingCourse = useRef(false);

    const [lesson, setLesson] = useState<Lesson>(emptyLesson);
    const [courseLearning, setCourseLearning] =
        useState<CourseLearningData>(emptyCourse);

    const getCourseDetails = useCallback(async () => {
        if (loadingCourse.current) return;

        loadingCourse.current = true;

        try {
            const response = await GetCourseDetails(courseId);
            if (
                response?.statusCode !== 200 ||
                !response?.data?.courses
            ) {
                console.error("Unable to get course details:", response);
                return;
            }

            const course = response.data.courses as CourseLearningData;

            setCourseLearning(course);

            if (!course.courseVideos?.length) {
                return;
            }

            const currentLesson =
                course.courseVideos.find(
                    (video) =>
                        video.courseVideoId === course.currentVideoId
                ) || course.courseVideos[0];
            setLesson(currentLesson);
        } catch (error) {
            console.error("Error loading course details:", error);
        } finally {
            loadingCourse.current = false;
        }
    }, [courseId]);

    var run = useRef(false);
    
    useEffect(() => {
        if(!run.current)
        {
            run.current = false;
          window.scrollTo(0, 0);
          void getCourseDetails();
        }
    }, [getCourseDetails]);

    const changeLesson = (selectedLesson: Lesson) => {
        setLesson(selectedLesson);
    };

    return (
        <section
            style={{
                width: "100%",
                minHeight: "100vh",
                background: "#050505",
                color: "#fff",
            }}
        >
            <div
                style={{
                    width: "100%",
                    background:
                        "radial-gradient(circle at top, #161616 0%, #050505 55%, #000 100%)",
                    padding: "25px 15px 40px",
                    boxSizing: "border-box",
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: "1400px",
                        margin: "0 auto",
                    }}
                >
                    <div style={{ marginBottom: "20px" }}>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: "26px",
                                fontWeight: 700,
                            }}
                        >
                            {courseLearning.courseName || "Course Learning"}
                        </h2>

                        {courseLearning.courseDescription && (
                            <p
                                style={{
                                    margin: "7px 0 0",
                                    color: "#999",
                                    fontSize: "14px",
                                }}
                            >
                                {courseLearning.courseDescription}
                            </p>
                        )}
                    </div>

                    <div
                        style={{
                            width: "100%",
                            background: "#000",
                            borderRadius: "16px",
                            overflow: "hidden",
                            border: "1px solid #222",
                            boxShadow: "0 15px 40px rgba(0,0,0,0.5)",
                        }}
                    >
                        {lesson.courseVideoId ? (
                            <>
                            <VideoPlayer
                                folder={courseLearning.serverName}
                                fileName={lesson.courseServerName+".mp4"}
                            />
                            </>
                        ) : (
                            <div
                                style={{
                                    minHeight: "300px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#666",
                                }}
                            >
                                Select a chapter to start learning.
                            </div>
                        )}

                        <div
                            style={{
                                padding: "18px 20px",
                                background: "#0c0c0c",
                            }}
                        >
                            <h3
                                style={{
                                    margin: "0 0 12px",
                                    fontSize: "19px",
                                }}
                            >
                                {lesson.courseVideoName || "Video Title"}
                            </h3>

                            <div
                                style={{
                                    display: "flex",
                                    gap: "25px",
                                    flexWrap: "wrap",
                                    color: "#aaa",
                                    fontSize: "13px",
                                }}
                            >
                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Total Course:
                                    </strong>{" "}
                                    {courseLearning.percentage || 0}%
                                </span>

                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Chapter:
                                    </strong>{" "}
                                    {lesson.videoPercentage || 0}%
                                </span>

                                <span>
                                    <strong style={{ color: "#fff" }}>
                                        Videos:
                                    </strong>{" "}
                                    {courseLearning.courseVideos.length}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: "25px" }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "12px",
                            }}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    fontSize: "18px",
                                }}
                            >
                                Course Chapters
                            </h3>

                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#888",
                                }}
                            >
                                {courseLearning.courseVideos.length} Chapters
                            </span>
                        </div>

                        <div
                            style={{
                                maxHeight: "400px",
                                overflowY: "auto",
                                background: "#090909",
                                border: "1px solid #202020",
                                borderRadius: "14px",
                                padding: "8px",
                            }}
                        >
                            {courseLearning.courseVideos.map(
                                (courseLesson, index) => {
                                    const isSelected =
                                        lesson.courseVideoId ===
                                        courseLesson.courseVideoId;

                                    const progress = Math.min(
                                        Math.max(
                                            Number(
                                                courseLesson.videoPercentage ||
                                                    0
                                            ),
                                            0
                                        ),
                                        100
                                    );

                                    return (
                                        <button
                                            type="button"
                                            key={courseLesson.courseVideoId}
                                            onClick={() =>
                                                changeLesson(courseLesson)
                                            }
                                            style={{
                                                width: "100%",
                                                padding: "15px",
                                                marginBottom:
                                                    index ===
                                                    courseLearning.courseVideos
                                                        .length -
                                                        1
                                                        ? 0
                                                        : "7px",
                                                cursor: "pointer",
                                                textAlign: "left",
                                                background: isSelected
                                                    ? "#1d1d1d"
                                                    : "#101010",
                                                border: isSelected
                                                    ? "1px solid #f5b45f"
                                                    : "1px solid #1c1c1c",
                                                borderRadius: "10px",
                                                color: "#fff",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "13px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "34px",
                                                        height: "34px",
                                                        minWidth: "34px",
                                                        borderRadius: "50%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        background: isSelected
                                                            ? "#f5b45f"
                                                            : "#222",
                                                        color: isSelected
                                                            ? "#000"
                                                            : "#aaa",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {index + 1}
                                                </div>

                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            color: isSelected
                                                                ? "#fff"
                                                                : "#ccc",
                                                            fontSize: "14px",
                                                            fontWeight:
                                                                isSelected
                                                                    ? 700
                                                                    : 500,
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {
                                                            courseLesson.courseVideoName
                                                        }
                                                    </div>

                                                    <div
                                                        style={{
                                                            marginTop: "5px",
                                                            fontSize: "11px",
                                                            color: "#777",
                                                        }}
                                                    >
                                                        Progress: {progress}%
                                                    </div>
                                                </div>

                                                <span
                                                    style={{
                                                        color: "#aaa",
                                                        fontSize: "17px",
                                                    }}
                                                >
                                                    ▶
                                                </span>
                                            </div>

                                            <div
                                                style={{
                                                    width: "100%",
                                                    height: "3px",
                                                    background: "#242424",
                                                    borderRadius: "10px",
                                                    marginTop: "12px",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: `${progress}%`,
                                                        height: "100%",
                                                        background: isSelected
                                                            ? "#f5b45f"
                                                            : "#555",
                                                        borderRadius: "10px",
                                                    }}
                                                />
                                            </div>
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Learning;